/**
 * /shop/ view toggle — GRID / ROWS switcher in the catalog toolbar.
 *
 * Clicking a [data-shop-view-btn] dispatches `shop:navigate` with a
 * URL whose ?view= reflects the clicked button. shop-ajax owns the
 * actual fetch + DOM swap + history push. We just:
 *   1. Build the destination URL (preserves every other filter param).
 *   2. Drop ?view= when going to the default ('grid') so URLs stay
 *      clean for the common case.
 *   3. Update the toolbar buttons' .is-active state immediately so
 *      the UI doesn't lag behind the fetch.
 *   4. Re-sync button state on browser back / forward via the
 *      `shop:state-restored` event shop-ajax fires after popstate.
 *
 * The TABLE button is disabled in the SSR and intentionally not
 * data-shop-view-btn — it stays a placeholder until that view is
 * implemented.
 */
const DEFAULT_VIEW = 'grid';
const ALLOWED      = new Set([ 'grid', 'rows', 'table' ]);

const syncButtons = (view) => {
    document.querySelectorAll('[data-shop-view-btn]').forEach((btn) => {
        const isActive = btn.dataset.view === view;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
};

const viewFromUrl = (url) => {
    const v = url.searchParams.get('view');
    return ALLOWED.has(v) ? v : DEFAULT_VIEW;
};

const init = () => {
    const buttons = document.querySelectorAll('[data-shop-view-btn]');
    if (!buttons.length) return;

    buttons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            if (btn.disabled) return;
            e.preventDefault();

            const targetView = btn.dataset.view;
            if (!ALLOWED.has(targetView)) return;

            const url = new URL(window.location.href);
            if (targetView === DEFAULT_VIEW) {
                url.searchParams.delete('view');
            } else {
                url.searchParams.set('view', targetView);
            }
            // Changing the view shouldn't keep us on page N — the new
            // layout may pack items differently, and "page 4 of grid"
            // doesn't correspond to anything in rows view.
            url.searchParams.delete('paged');
            url.pathname = url.pathname.replace(/\/page\/\d+\/?$/, '/');

            syncButtons(targetView);
            document.dispatchEvent(new CustomEvent('shop:navigate', {
                detail: { url: url.toString() },
            }));
        });
    });

    // After popstate / CLEAR ALL / shop:navigate, shop-ajax fires
    // shop:state-restored once the form/URL are back in sync. Prefer
    // the URL carried on the event over window.location.href because
    // the latter can be stale if a listener runs before pushState
    // (defense against ordering bugs in the AJAX pipeline).
    document.addEventListener('shop:state-restored', (e) => {
        const url = e?.detail?.url || new URL(window.location.href);
        syncButtons(viewFromUrl(url));
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
